import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import FollowupAlertPopup from '@/components/FollowupAlertPopup';
import SubscriptionWall from '@/components/SubscriptionWall';
import SessionExpiryWarning from '@/components/SessionExpiryWarning';
import { NotificationBell } from '@/components/NotificationBell';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Menu, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

export default function DashboardLayout() {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const location = useLocation();

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  // Don't show subscription wall on billing & settings pages
  const isBillingPage = location.pathname === '/billing' || location.pathname === '/settings';

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      </div>

      {/* Mobile sidebar */}
      <Sheet>
        <div className="md:hidden fixed top-0 left-0 right-0 h-14 border-b border-border bg-card/95 backdrop-blur-sm z-30 flex items-center px-4 gap-3">
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="mobile-menu-btn">
              <Menu className="w-4 h-4" />
            </Button>
          </SheetTrigger>
          <span className="font-heading text-base font-bold">VoyageOS</span>
        </div>
        <SheetContent side="left" className="p-0 w-60">
          <Sidebar collapsed={false} onToggle={() => {}} />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <main
        className={cn(
          "transition-all duration-300 min-h-screen",
          "pt-14 md:pt-0",
          sidebarCollapsed ? "md:ml-16" : "md:ml-60"
        )}
      >
        {/* Top bar */}
        <header className="hidden md:flex h-14 border-b border-border bg-card/50 backdrop-blur-sm items-center justify-between px-6">
          <div>
            <p className="text-sm text-muted-foreground font-body">
              Welcome back, <span className="font-medium text-foreground">{user?.name}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              className="h-8 w-8"
              data-testid="theme-toggle-btn"
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Follow-up Alert Popup */}
      <FollowupAlertPopup />

      {/* Session Expiry Warning */}
      <SessionExpiryWarning />

      {/* Subscription Wall - blocks access when trial expired (except billing/settings) */}
      {!isBillingPage && <SubscriptionWall />}
    </div>
  );
}
